// Stage 5: 入职办理 - 3个模块
import { useApi, api, mutate } from '../api/client';
import { useActions } from '../actions';
import {
  PageHeader, ModuleBanner, Card, CardHead, Kpi, Tag, Status, Loading, ErrorBox, Progress,
} from '../components/UI';
import { MODULE_META } from '../config';

// ===== 5.1 入职前跟进 =====
export function PreOnboard() {
  const meta = MODULE_META['pre-onboard'];
  const { toast, openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.preOnboard(), [], 'pre-onboard');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={
          <>
            <button className="btn btn-primary" onClick={() => openCreate('pre-onboard')}>+ 新增跟进</button>
            <button className="btn btn-ghost" onClick={() => toast('提醒已群发')}>群发提醒</button>
          </>
        }
      />
      <ModuleBanner title="候选人离职进度跟踪" stageTag="Stage 5-1" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="已 Offer" value={19} />
        <Kpi label="待入职" value={12} />
        <Kpi label="离职进度正常" value={9} trend={{ type: 'up', text: '低风险' }} />
        <Kpi label="高风险" value={2} trend={{ type: 'down', text: '需关注' }} />
      </div>

      <Card>
        <CardHead title="候选人跟进" />
        <table className="table">
          <thead>
            <tr>
              <th>候选人</th><th>原公司</th><th>预计离职</th>
              <th>距离天数</th><th>当前状态</th><th>风险</th>
              <th>最近联系</th><th>操作</th>
            </tr>
          </thead>
          <tbody>
            {data.map(p => (
              <tr key={p.id}>
                <td><b>{p.name}</b></td>
                <td>{p.original}</td>
                <td className="mono text-xs">{p.leave_date}</td>
                <td className="mono fw-600" style={{ color: p.days_to_go < 14 ? 'var(--cinnabar)' : undefined }}>
                  {p.days_to_go} 天
                </td>
                <td>
                  <Status type={p.status === '已确认' || p.status === '进度正常' ? 'progress' : 'pending'}>
                    {p.status}
                  </Status>
                </td>
                <td>
                  <Tag color={p.risk === '低' ? 'bamboo' : p.risk === '中' ? 'gold' : 'cinnabar'}>
                    {p.risk}风险
                  </Tag>
                </td>
                <td className="text-sm text-ink-3">{p.status === '协商中' ? '07-06 HRBP' : '07-05 林婉清'}</td>
                <td>
                  <button className="btn-text" onClick={() => toast('联系记录已打开')}>联系</button>
                  <button className="btn-text" onClick={() => toast('备注功能开发中')}>备注</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ===== 5.2 报到入职 =====
export function Onboard() {
  const meta = MODULE_META['onboard'];
  const { toast, openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.onboardChecklist(), [], 'onboard-checklist');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  // 按员工分组
  const grouped: Record<string, typeof data> = {};
  for (const item of data) {
    if (!grouped[item.employee_name]) grouped[item.employee_name] = [];
    grouped[item.employee_name].push(item);
  }

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => openCreate('new-hires')}>+ 新员工报到</button>}
      />
      <ModuleBanner title="入职手续清单" stageTag="Stage 5-2" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="本周入职" value={5} />
        <Kpi label="手续完成" value={3} />
        <Kpi label="资料补齐率" value={94} unit="%" />
        <Kpi label="入职用时" value="2.5" unit="h" />
      </div>

      {Object.entries(grouped).map(([name, items]) => {
        const position = items[0].position;
        const date = items[0].date;
        const done = items.filter(i => i.done).length;
        const progress = Math.round((done / items.length) * 100);
        return (
          <div key={name} className="card">
            <div className="card-head">
              <div>
                <div className="card-title">
                  {name} <span className="text-ink-3 fw-400" style={{ fontSize: 12 }}>· {position}</span>
                </div>
                <div className="text-xs text-ink-3" style={{ marginTop: 8 }}>
                  入职日期：<b>{date}</b> · 完成度 {progress}%
                </div>
              </div>
              <div className="flex gap-2">
                <button className="btn btn-ghost" onClick={() => toast('合同下载中')}>下载合同</button>
                <button className="btn btn-primary" onClick={() => toast('归档已提交')}>提交归档</button>
              </div>
            </div>
            <div className="onboard-checklist">
              {items.map(it => (
                <div key={it.id} className={`check-item ${it.done ? 'done' : ''}`}>
                  <div className="check-box" />
                  <div className="check-text">{it.item_text}</div>
                  <div className="check-meta">{it.done_date}</div>
                  <button className="btn-text" onClick={() => it.done ? toast('材料已上传') : mutate.update('onboard-checklist', it.id, { done: 1, done_date: '今日' })}>{it.done ? '查看' : '上传'}</button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===== 5.3 新人引导 =====
export function OnboardGuide() {
  const meta = MODULE_META['onboard-guide'];
  const { toast } = useActions();
  const { data, loading, error } = useApi(() => api.newHires(), [], 'new-hires');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => toast('引导配置开发中')}>+ 配置引导</button>}
      />
      <ModuleBanner title="入职引导体系" stageTag="Stage 5-3" />

      <Card>
        <CardHead title="5 步入职引导流程" />
        <div className="guide-steps">
          {[
            { num: '01', title: '入职日', desc: '合同签订、资料收集、工位开通' },
            { num: '02', title: '公司培训', desc: '企业文化、制度规范、办公系统' },
            { num: '03', title: '部门介绍', desc: '团队成员、业务介绍、协作流程' },
            { num: '04', title: '导师 1:1', desc: '导师分配、岗位职责、目标设定' },
            { num: '05', title: '30 天复盘', desc: '适应反馈、问题跟进、计划调整' },
          ].map(s => (
            <div key={s.num} className="guide-step">
              <div className="gs-num">{s.num}</div>
              <div className="gs-title">{s.title}</div>
              <div className="gs-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHead title="新员工引导列表" sub={`本月 ${data.length} 位`} />
        <table className="table">
          <thead>
            <tr>
              <th>员工</th><th>岗位</th><th>部门</th><th>入职日期</th>
              <th>导师</th><th>引导状态</th><th>操作</th>
            </tr>
          </thead>
          <tbody>
            {data.map(n => (
              <tr key={n.id}>
                <td><b>{n.name}</b></td>
                <td>{n.position}</td>
                <td>{n.dept}</td>
                <td className="mono text-xs">{n.date}</td>
                <td>
                  <div className="name-cell">
                    <div className="avatar-sm" style={{ background: 'var(--bamboo)', color: 'var(--paper)' }}>
                      {n.mentor.charAt(0)}
                    </div>
                    {n.mentor}
                  </div>
                </td>
                <td>
                  <div className="progress" style={{ width: 100 }}>
                    <div
                      className="progress-bar bamboo"
                      style={{ width: `${n.date <= '07-25' ? 95 : n.date <= '07-28' ? 80 : 50}%` }}
                    />
                  </div>
                </td>
                <td><button className="btn-text" onClick={() => toast('详情开发中')}>查看详情</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
